import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import Signer from "./signer";

interface SignerSectionProps {
  signers: Signer[];
}

function SignersSection({ signers }: SignerSectionProps): React.JSX.Element {
  const { t } = useTranslation();
  const [showAllDetails, setShowAllDetails] = useState(true);
  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Stack alignItems="center" direction="row" justifyContent="space-between">
        <div>
          <Typography gutterBottom variant="h6">
            {t("signers.title")}
          </Typography>
          <Typography color="text.secondary" variant="body1">
            {t("signers.subtitle")}
          </Typography>
        </div>
        <div>
          <Button
            onClick={() => {
              setShowAllDetails(!showAllDetails);
            }}
          >
            {showAllDetails ? t("signers.showAll") : t("signers.hideAll")}
          </Button>
        </div>
      </Stack>
      <Stack spacing={3}>
        {signers
          .sort((a, b) => {
            if (a.status === "PENDING" && b.status !== "PENDING") {
              return -1;
            }
            if (a.status !== "PENDING" && b.status === "PENDING") {
              return 1;
            }
            return 0;
          })
          .map((item) => (
            <Signer
              fields={[
                { label: t("signers.fields.docType"), value: item.documentType },
                {
                  label: t("signers.fields.docId"),
                  value: item.document,
                },
                { label: t("signers.fields.email"), value: item.email },
                {
                  label: t("signers.fields.signatureDate"),
                  value: item.signature?.signatureDate
                    ? dayjs(item.signature.signatureDate).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : t("signers.fields.awaitingTime"),
                },
                {
                  label: t("signers.fields.signatureType"),
                  value: (() => {
                    if (item.signature?.signatureType) {
                      return item.signature.signatureType === "ELECTRONIC"
                        ? t("signers.fields.signatureTypeElectronic")
                        : t("signers.fields.signatureTypeDigital");
                    }
                    return t("signers.fields.awaiting");
                  })(),
                },
                {
                  label: t("signers.fields.authentication"),
                  value: (() => {
                    if (item.signature?.authenticationType) {
                      return item.signature.authenticationType === "CERTIFICADO"
                        ? t("signers.fields.signatureTypeDigital")
                        : t("signers.fields.authToken");
                    }
                    return t("signers.fields.awaiting");
                  })(),
                },
                {
                  label: t("signers.fields.firstView"),
                  value: item.signature?.createdAt
                    ? dayjs(item.signature.createdAt).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : t("signers.fields.awaitingTime"),
                },
                {
                  label: t("signers.fields.lastView"),
                  value: item.signature?.updatedAt
                    ? dayjs(item.signature.updatedAt).format(
                        "DD/MM/YYYY HH:mm:ss"
                      )
                    : t("signers.fields.awaitingTime"),
                },
                {
                  label: t("signers.fields.geolocation"),
                  value: (() => {
                    if (item.signature) {
                      return item.signature.geolocation
                        ? item.signature.geolocation
                        : t("signers.fields.geoNotAllowed");
                    }
                    return t("signers.fields.awaiting");
                  })(),
                },
                {
                  label: t("signers.fields.ip"),
                  value: item.signature ? item.signature.ipV4 : t("signers.fields.awaiting"),
                },
                {
                  label: t("signers.fields.ipv6"),
                  value: item.signature ? item.signature.ipV6 : t("signers.fields.awaiting"),
                },
              ]}
              key={item.id}
              masterOpen={showAllDetails}
              name={item.name}
              status={
                item.status === "REJECTED"
                  ? "refused"
                  : item.status === "SIGNED"
                  ? "signed"
                  : "awaiting"
              }
            />
          ))}
      </Stack>
    </Stack>
  );
}

export default SignersSection;

